<template>
  <div
    class="w-full flex justify-center px-4 mt-[30px] tablet-v:mt-[56px] tablet-h:mt-[90px]"
  >
    <div class="w-full max-w-[410px]">
      <div class="mb-10">
        <h1
          class="text-[26px] font-medium leading-[30px] tracking-[-0.325px] text-gray-800"
        >
          Your work. Your rights.
        </h1>
        <p
          class="mt-[4px] text-[26px] font-medium leading-[30px] tracking-[-0.325px] text-gray-800/55"
        >
          Sign in to your Dacr account
        </p>
      </div>

      <form class="flex flex-col" @submit.prevent="handleSignIn">
        <div class="mb-[14px]">
          <Input
            v-model="email"
            size="xl"
            label="Email"
            type="email"
            :error="emailError"
            :disabled="loading"
            :tabindex="1"
          />
        </div>
        <div class="mb-[14px]">
          <Input
            v-model="password"
            size="xl"
            label="Password"
            type="password"
            :error="passwordError"
            :disabled="loading"
            :tabindex="2"
          />
        </div>

        <Button
          class="w-full mb-[15px]"
          variant="primary"
          size="lg"
          :loading="loading"
          @click="handleSignIn"
        >
          Sign in
        </Button>
        <button
          type="submit"
          class="hidden"
          aria-hidden="true"
          tabindex="-1"
          :disabled="loading"
        />

        <RouterLink
          to="forgot-password"
          class="text-link text-[13px]"
          :class="linkClasses"
        >
          Forgot password?
        </RouterLink>
      </form>

      <p class="mt-[30px] text-[14px] leading-[22px] text-center text-gray-800">
        New to Dacr?
        <span
          class="text-link"
          :class="linkClasses"
          @click="handleCreateAccountClick"
        >
          Create an account
        </span>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Input, Button } from '@dacr/library'
import {
  MeDocument,
  useValidateOtpMutation,
  type MeQuery,
} from '@/shared/graphql/legacy/generated'
import { useUserStore } from '@/shared/stores/user'
import { useRoute, useRouter } from 'vue-router'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { signin } from '@dacr/auth'
import { apolloClient } from '@/shared/graphql/client'
import { EMAIL_REGEX } from '@/shared/utils/string'
import { analytics } from '@/shared/services/analytics'

const { mutate: validateOTP } = useValidateOtpMutation()

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const toastStore = useToastStore()

const email = ref('')
const password = ref('')
const loading = ref(false)

const emailError = ref('')
const passwordError = ref('')

watch(email, () => {
  emailError.value = ''
})

watch(password, () => {
  passwordError.value = ''
})

const linkClasses = computed(() => ({
  ...(loading.value && {
    'pointer-events-none': true,
    'opacity-50': true,
  }),
}))

const handleCreateAccountClick = () => {
  router.push({
    path: '/register',
    query: {
      ott: route.query.ott,
      redirect: route.query.redirect,
    },
  })
}

const validate = () => {
  let hasError = false

  if (email.value.trim() && !EMAIL_REGEX.test(email.value.trim())) {
    emailError.value = 'Please enter a valid email.'
    hasError = true
  }

  if (!email.value.trim()) {
    emailError.value = 'Please enter your email address'
    hasError = true
  }

  if (!password.value.trim()) {
    passwordError.value = 'Please enter your password'
    hasError = true
  }

  return !hasError
}

const handleSignIn = async () => {
  if (!validate()) return

  try {
    loading.value = true

    await signin({
      email: email.value,
      password: password.value,
      client: 'web',
    })

    if (route.query.ott) {
      await validateOTP({
        code: route.query.ott as string,
      })

      router.push('/auth/success')
      return
    }

    const { data } = await apolloClient.query<MeQuery>({
      query: MeDocument,
    })

    if (data) {
      analytics.trackLogin(data.me.id)

      analytics.track('user_login', {
        id: data.me.id,
        name: data.me.name,
        username: data.me.username,
        email: data.me.email,
      })

      userStore.setUser(data.me)
      userStore.isSignedIn = true

      // Check for OAuth redirect
      const redirectUrl = route.query.redirect as string

      if (redirectUrl) {
        window.location.href = redirectUrl
        return
      }

      router.push('/home')
    }
  } catch (error) {
    console.error(error)

    toastStore.showToast({
      type: ToastType.Error,
      content: 'Incorrect email or password',
      error: error as Error,
    })
  } finally {
    loading.value = false
  }
}
</script>
